import React, { useEffect, useState } from 'react';
import { getNewsWithAuthors } from './utils/pocketbaseApi';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString();
};

const truncateText = (text, maxLength = 200) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const Home = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNews, setExpandedNews] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const newsPerPage = 5;

  useEffect(() => {
    async function fetchNews() {
      setLoading(true);
      setError(null);
      try {
        const data = await getNewsWithAuthors();
        // Sort news by created date, newest first
        const sortedNews = data.sort((a, b) => new Date(b.created) - new Date(a.created));
        setNews(sortedNews);
      } catch (err) {
        setError('Failed to load news');
      } finally {
        setLoading(false);
      }
    }
    fetchNews();
  }, []);

  const toggleNewsExpand = (newsId) => {
    setExpandedNews(prev => ({
      ...prev,
      [newsId]: !prev[newsId]
    }));
  };

  const paginatedNews = news.slice(0, currentPage * newsPerPage);
  const hasMoreNews = news.length > currentPage * newsPerPage;

  // Responsive style: 100vw minus sidebar width (16rem = 256px) when expanded, 90vw when collapsed
  // We'll use a wrapper div with overflow-x-hidden to prevent scrollbars
  return (
    <div className="p-6">
      <h1 className="text-4xl font-bold text-[#0a1744] bg-gradient-to-r from-[#0a1744] to-[#A1E744] bg-clip-text text-transparent mb-8">News</h1>
      {loading && (
        <div className="space-y-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6">
              <Skeleton height={28} width="40%" className="mb-4" />
              <Skeleton count={3} height={18} className="mb-2" />
              <Skeleton height={16} width="30%" className="mb-1" />
              <Skeleton height={16} width="20%" className="mb-1" />
            </div>
          ))}
        </div>
      )}
      {error && <div className="text-red-600">{error}</div>}
      <div className="space-y-8">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle space-y-8">
            {paginatedNews.map((item) => {
              const author = item.authorInfo;
              const fullName = author ? `${author.Name || ''} ${author.Surname || ''}`.trim() : 'Unknown';
              const isExpanded = expandedNews[item.id];
              const newsContent = isExpanded ? item.News : truncateText(item.News);

              return (
                <div key={item.id} className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6 hover:border-[#A1E744] transition-colors duration-200">
                  <h2 className="text-2xl font-semibold mb-2 bg-gradient-to-r from-[#0a1744] to-[#A1E744] bg-clip-text text-transparent">{item.Headline}</h2>
                  <div className="mb-4 text-gray-700" dangerouslySetInnerHTML={{ __html: newsContent || '' }} />
                  {item.News && item.News.length > 200 && (
                    <button
                      onClick={() => toggleNewsExpand(item.id)}
                      className="text-[#A1E744] hover:text-[#0a1744] font-medium transition-colors duration-200"
                    >
                      {isExpanded ? '...less' : '...more'}
                    </button>
                  )}
                  <div className="text-sm text-gray-500 mb-1">Published by: <span className="font-medium text-[#0a1744]">{fullName}</span></div>
                  <div className="text-xs text-gray-400">Created: {formatDate(item.created)}</div>
                  <div className="text-xs text-gray-400">Updated: {formatDate(item.updated)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {hasMoreNews && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            className="px-6 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors duration-200"
          >
            Show More
          </button>
        </div>
      )}
    </div>
  );
};

export default Home; 